import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  Send,
  Loader2,
  CheckCircle,
  AlertCircle,
  ArrowLeft,
  Wallet,
} from 'lucide-react';

// Ethereum type declaration
declare global {
  interface Window {
    ethereum?: {
      request: (args: { method: string; params?: unknown[] }) => Promise<unknown>;
      isMetaMask?: boolean;
    };
  }
}

interface FormField {
  name: string;
  type: string;
  label: string;
  placeholder?: string;
  required: boolean;
  options?: string[];
}

interface FormData {
  id: string;
  name: string;
  slug: string;
  description?: string;
  formType: string;
  targetAudience?: string;
  fields: FormField[];
  logoUrl?: string;
  primaryColor?: string;
}

export default function PublicForm() {
  const { slug } = useParams<{ slug: string }>();
  const navigate = useNavigate();
  
  const [form, setForm] = useState<FormData | null>(null);
  const [formValues, setFormValues] = useState<Record<string, unknown>>({});
  const [isLoading, setIsLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<{ message: string; redirectUrl?: string } | null>(null);

  useEffect(() => {
    loadForm();
  }, [slug]);

  const loadForm = async () => {
    try {
      const response = await fetch(`/api/forms/public/${slug}`);
      const data = await response.json();
      
      if (data.error) {
        setError(data.error);
      } else {
        setForm(data.form);
        // Initialize form values
        const initialValues: Record<string, unknown> = {};
        data.form.fields.forEach((field: FormField) => {
          if (field.type === 'checkbox') {
            initialValues[field.name] = false;
          } else if (field.type === 'multiselect') {
            initialValues[field.name] = [];
          } else {
            initialValues[field.name] = '';
          }
        });
        // Check for pending wallet from landing page
        const pendingWallet = localStorage.getItem('pendingWallet');
        if (pendingWallet) {
          initialValues['wallet'] = pendingWallet;
        }
        setFormValues(initialValues);
      }
    } catch {
      setError('Failed to load form');
    } finally {
      setIsLoading(false);
    }
  };

  const handleChange = (name: string, value: unknown) => {
    setFormValues(prev => ({ ...prev, [name]: value }));
  };

  const handleMultiSelect = (name: string, option: string) => {
    const current = (formValues[name] as string[]) || [];
    if (current.includes(option)) {
      handleChange(name, current.filter(v => v !== option));
    } else {
      handleChange(name, [...current, option]);
    }
  };

  const connectWallet = async () => {
    if (!window.ethereum) {
      alert('Please install MetaMask or another Web3 wallet');
      return;
    }

    try {
      const accounts = await window.ethereum.request({
        method: 'eth_requestAccounts',
      }) as string[];
      
      if (accounts[0]) {
        handleChange('wallet', accounts[0]);
        localStorage.setItem('pendingWallet', accounts[0]);
      }
    } catch (error) {
      console.error('Failed to connect wallet:', error);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setError(null);

    try {
      const response = await fetch(`/api/forms/public/${slug}/submit`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ data: formValues }),
      });
      
      const data = await response.json();
      
      if (data.error) {
        setError(data.message || data.error);
      } else {
        setSuccess({
          message: data.message,
          redirectUrl: data.redirectUrl,
        });
        
        // Clear pending wallet
        localStorage.removeItem('pendingWallet');
        
        // Redirect if specified
        if (data.redirectUrl) {
          setTimeout(() => {
            navigate(data.redirectUrl);
          }, 3000);
        }
      }
    } catch {
      setError('Failed to submit form');
    } finally {
      setIsSubmitting(false);
    }
  };

  const renderField = (field: FormField) => {
    const value = formValues[field.name];
    const baseInputClass = "w-full px-4 py-3 rounded-xl bg-slate-800/50 border border-slate-700 text-white placeholder-slate-500 focus:border-sky-500 focus:ring-1 focus:ring-sky-500 outline-none transition-colors";

    switch (field.type) {
      case 'text':
      case 'email':
      case 'phone':
      case 'number':
      case 'date':
        return (
          <input
            type={field.type === 'phone' ? 'tel' : field.type}
            value={value as string}
            onChange={(e) => handleChange(field.name, e.target.value)}
            placeholder={field.placeholder}
            required={field.required}
            className={baseInputClass}
          />
        );

      case 'textarea':
        return (
          <textarea
            value={value as string}
            onChange={(e) => handleChange(field.name, e.target.value)}
            placeholder={field.placeholder}
            required={field.required}
            rows={4}
            className={baseInputClass}
          />
        );

      case 'select':
        return (
          <select
            value={value as string}
            onChange={(e) => handleChange(field.name, e.target.value)}
            required={field.required}
            className={baseInputClass}
          >
            <option value="">Select an option</option>
            {field.options?.map((option) => (
              <option key={option} value={option}>
                {option}
              </option>
            ))}
          </select>
        );

      case 'multiselect':
        return (
          <div className="flex flex-wrap gap-2">
            {field.options?.map((option) => {
              const selected = ((value as string[]) || []).includes(option);
              return (
                <button
                  key={option}
                  type="button"
                  onClick={() => handleMultiSelect(field.name, option)}
                  className={`px-4 py-2 rounded-lg border transition-colors ${
                    selected
                      ? 'bg-sky-500/20 border-sky-500 text-sky-400'
                      : 'bg-slate-800/50 border-slate-700 text-slate-400 hover:border-slate-600'
                  }`}
                >
                  {option}
                </button>
              );
            })}
          </div>
        );

      case 'checkbox':
        return (
          <label className="flex items-center gap-3 cursor-pointer">
            <input
              type="checkbox"
              checked={value as boolean}
              onChange={(e) => handleChange(field.name, e.target.checked)}
              className="w-5 h-5 rounded bg-slate-800 border-slate-700 text-sky-500 focus:ring-sky-500"
            />
            <span className="text-slate-300">{field.placeholder || field.label}</span>
          </label>
        );

      case 'radio':
        return (
          <div className="space-y-2">
            {field.options?.map((option) => (
              <label key={option} className="flex items-center gap-3 cursor-pointer">
                <input
                  type="radio"
                  name={field.name}
                  value={option}
                  checked={value === option}
                  onChange={(e) => handleChange(field.name, e.target.value)}
                  className="w-5 h-5 bg-slate-800 border-slate-700 text-sky-500 focus:ring-sky-500"
                />
                <span className="text-slate-300">{option}</span>
              </label>
            ))}
          </div>
        );

      case 'wallet':
        return (
          <div className="flex gap-3">
            <input
              type="text"
              value={value as string}
              onChange={(e) => handleChange(field.name, e.target.value)}
              placeholder="0x..."
              className={`${baseInputClass} flex-1 font-mono`}
            />
            <button
              type="button"
              onClick={connectWallet}
              className="px-4 py-3 rounded-xl bg-gradient-to-r from-amber-500 to-orange-500 text-white font-medium flex items-center gap-2 hover:opacity-90 transition-opacity"
            >
              <Wallet size={16} />
              Connect
            </button>
          </div>
        );

      default:
        return (
          <input
            type="text"
            value={value as string}
            onChange={(e) => handleChange(field.name, e.target.value)}
            placeholder={field.placeholder}
            required={field.required}
            className={baseInputClass}
          />
        );
    }
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-950 via-slate-900 to-slate-950 flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-sky-500 animate-spin" />
      </div>
    );
  }

  if (error && !form) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-950 via-slate-900 to-slate-950 flex items-center justify-center p-4">
        <div className="text-center">
          <AlertCircle className="w-16 h-16 text-red-500 mx-auto mb-4" />
          <h1 className="text-2xl font-bold text-white mb-2">Form Not Found</h1>
          <p className="text-slate-400 mb-6">{error}</p>
          <button
            onClick={() => navigate('/')}
            className="flex items-center gap-2 mx-auto px-6 py-3 rounded-xl bg-slate-800 hover:bg-slate-700 text-white transition-colors"
          >
            <ArrowLeft size={16} />
            Back to Home
          </button>
        </div>
      </div>
    );
  }

  if (success) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-950 via-slate-900 to-slate-950 flex items-center justify-center p-4">
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          className="text-center max-w-md"
        >
          <div className="w-20 h-20 mx-auto mb-6 rounded-full bg-emerald-500/20 flex items-center justify-center">
            <CheckCircle className="w-10 h-10 text-emerald-500" />
          </div>
          <h1 className="text-2xl font-bold text-white mb-2">Submission Successful!</h1>
          <p className="text-slate-400 mb-6">{success.message}</p>
          {success.redirectUrl && (
            <p className="text-slate-500 text-sm">Redirecting...</p>
          )}
          <button
            onClick={() => navigate('/')}
            className="flex items-center gap-2 mx-auto px-6 py-3 rounded-xl bg-gradient-to-r from-sky-500 to-purple-500 text-white font-medium hover:opacity-90 transition-opacity"
          >
            Go to Dashboard
          </button>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-950 via-slate-900 to-slate-950 py-12 px-4">
      <div className="max-w-2xl mx-auto">
        {/* Back button */}
        <button
          onClick={() => navigate('/')}
          className="flex items-center gap-2 text-slate-400 hover:text-white transition-colors mb-8"
        >
          <ArrowLeft size={18} />
          Back
        </button>

        {/* Form header */}
        <div className="text-center mb-8">
          {form?.logoUrl ? (
            <img src={form.logoUrl} alt="" className="h-16 mx-auto mb-4" />
          ) : (
            <div className="w-16 h-16 mx-auto mb-4 rounded-xl bg-gradient-to-br from-sky-500 to-purple-500 flex items-center justify-center">
              <span className="text-2xl font-bold text-white">VT</span>
            </div>
          )}
          <h1 className="text-3xl font-bold text-white mb-2">{form?.name}</h1>
          {form?.description && (
            <p className="text-slate-400">{form.description}</p>
          )}
        </div>

        {/* Form */}
        <motion.form
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          onSubmit={handleSubmit}
          className="space-y-6 p-8 rounded-2xl bg-slate-800/30 border border-slate-700/50"
        >
          {error && (
            <div className="p-4 rounded-xl bg-red-500/10 border border-red-500/30 text-red-400 flex items-center gap-3">
              <AlertCircle size={16} />
              {error}
            </div>
          )}

          {form?.fields.map((field) => (
            <div key={field.name}>
              <label className="block text-sm font-medium text-slate-300 mb-2">
                {field.label}
                {field.required && <span className="text-red-400 ml-1">*</span>}
              </label>
              {renderField(field)}
            </div>
          ))}

          <motion.button
            type="submit"
            disabled={isSubmitting}
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
            className="w-full py-4 rounded-xl bg-gradient-to-r from-sky-500 to-purple-500 text-white font-semibold text-lg flex items-center justify-center gap-2 hover:opacity-90 transition-opacity disabled:opacity-50"
          >
            {isSubmitting ? (
              <Loader2 className="w-5 h-5 animate-spin" />
            ) : (
              <>
                <Send size={16} />
                Submit
              </>
            )}
          </motion.button>
        </motion.form>
      </div>
    </div>
  );
}
